import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import type { ITeacherTermModule } from '@/entities/TeacherTerms';
import { Svg } from '@/shared/ui/Svg';
import { formatPercentOrDash } from '@/shared/lib/utils/formatPercent';
import { AppImage } from '@/shared/ui/AppImage';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';
import type { UserShortInfo } from '@/shared/types/common';

const formatDateTime = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return {
      date: format(date, 'dd.MM.yyyy'),
      time: format(date, 'HH:mm'),
    };
  } catch {
    return { date: dateString, time: '' };
  }
};

const renderStaffCell = (person: UserShortInfo | null) => {
  if (!person) {
    return <span className="text-grey-secondary">—</span>;
  }
  return (
    <div className="flex items-center gap-2">
      {person.avatar ? (
        <AppImage
          src={person.avatar}
          alt={`${person.firstName} ${person.lastName}`}
          className="w-9 h-9 min-w-9 min-h-9 rounded-full object-cover"
        />
      ) : (
        <div className="w-9 h-9 rounded-full bg-grey-100 flex items-center justify-center text-sm font-semibold">
          {person.username?.charAt(0) || ''}
        </div>
      )}
      <span className="text-black-headline">{person.username}</span>
    </div>
  );
};

export const modulesColumns: ColumnDef<ITeacherTermModule>[] = [
  {
    header: 'Module',
    accessorKey: 'title',
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="text-black-headline">{row.original.title}</span>
      </div>
    ),
  },
  {
    header: 'Module Teacher',
    accessorKey: 'moduleTeacher',
    cell: ({ row }) => renderStaffCell(row.original.moduleTeacher),
  },
  {
    header: 'Lecturer',
    accessorKey: 'lecturer',
    cell: ({ row }) => renderStaffCell(row.original.lecturer),
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Progress</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'progressPercent',
    cell: ({ row }) => (
      <span className="text-black-headline">
        {formatPercentOrDash(row.original.progressPercent)}
      </span>
    ),
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Attendance</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'attendancePercent',
    cell: ({ row }) => (
      <span className="text-black-headline">
        {formatPercentOrDash(row.original.attendancePercent)}
      </span>
    ),
  },
  {
    header: () => (
      <div className="flex items-center gap-2">
        <span>Last Activity</span>
        <Svg Icon={ChevronDownIcon} width={12} height={12} className="text-grey-secondary" />
      </div>
    ),
    accessorKey: 'createdAt',
    cell: ({ row }) => {
      const dateString = row.original.lastActivity;
      if (!dateString) {
        return <span className="text-grey-secondary">—</span>;
      }
      const lastActivity = formatDateTime(dateString);
      return (
        <div className="flex flex-col">
          <span className="text-black-headline">{lastActivity.date}</span>
          <span className="text-grey-secondary text-sm">{lastActivity.time}</span>
        </div>
      );
    },
  },
];
